import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { useRowSelectionContext } from "@/components/data-table/context/RowSelectionContext";
import { QueryTable } from "@/components/query-table/QueryTable";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmailRecipientsAddUsersSubmitButton } from "@/modules/email-recipients/components/EmailRecipientsAddUsersSubmitButton";
import { useEmailRecipientsAddUsersMutation } from "@/modules/email-recipients/hooks/useEmailRecipientsAddUsersMutation";
import { useEmailRecipientsAvailableColumnOverrides } from "@/modules/email-recipients/hooks/useEmailRecipientsAvailableColumnOverrides";
import { useEmailRecipientsAvailableQuery } from "@/modules/email-recipients/hooks/useEmailRecipientsAvailableQuery";
import { displayAvailableEmailRecipient } from "@/schemas/emailRecipient";
import { DisplayAvailableEmailRecipient } from "@/types/emailRecipient";
import { QueryKey } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

interface EmailRecipientsAddUsersDialogContentProps {
  emailTypeId: string;
  queryKey: QueryKey;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EmailRecipientsAddUsersDialogContent = ({
  emailTypeId,
  queryKey,
  isOpen,
  onOpenChange,
}: EmailRecipientsAddUsersDialogContentProps) => {
  const { t } = useTranslation();
  const availableColumnOverrides = useEmailRecipientsAvailableColumnOverrides();
  const { store } = useRowSelectionContext();

  const { query, options } = useEmailRecipientsAvailableQuery({
    emailTypeId,
    enabled: isOpen,
  });

  const handleSuccess = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const { handleSubmit, isSubmitting } = useEmailRecipientsAddUsersMutation({
    store,
    emailTypeId,
    onSuccess: handleSuccess,
    queryKey,
  });

  useEffect(() => {
    if (!isOpen) {
      store.clear();
    }
  }, [isOpen, store]);

  const onDialogOpenChange = useCallback(
    (open: boolean) => {
      onOpenChange(open);
    },
    [onOpenChange],
  );

  const tableFeatures = useMemo(
    () => ({
      enableRowSelection: true,
      enableSelectAll: true,
      enableFilters: true,
      enableSorting: true,
    }),
    [],
  );

  return (
    <Dialog open={isOpen} onOpenChange={onDialogOpenChange}>
      <DialogContent
        data-testid="email-recipients-add-users-dialog"
        className="max-w-[90dvw] h-[95dvh]"
      >
        <DataTableQueryContextProvider value={{ query }}>
          <QueryTable<DisplayAvailableEmailRecipient>
            schema={displayAvailableEmailRecipient}
            name="emailRecipientsAvailable"
            query={query}
            options={options}
            tableFeatures={tableFeatures}
            translationKey="emailRecipients"
            columnOverrides={availableColumnOverrides}
          >
            <div className="flex flex-col gap-3 min-h-0 min-w-0">
              <DialogHeader>
                <DialogTitle className="text-xl font-medium text-gray-800">
                  {t("emailRecipients.addUsers.title")}
                </DialogTitle>
              </DialogHeader>
              <div className="flex flex-row items-center gap-2">
                <QueryTable.Menubar className="m-0 grow">
                  <QueryTable.Menubar.Tools inDialog />
                  <QueryTable.Menubar.Columns inDialog />
                  <QueryTable.Menubar.Settings inDialog />
                </QueryTable.Menubar>
              </div>
              <div className="flex flex-row grow gap-2 overflow-hidden">
                <QueryTable.Content
                  className="overflow-x-auto flex-grow"
                  isInDialog
                >
                  <QueryTable.Status>
                    <QueryTable.SelectedRowsStatus />
                  </QueryTable.Status>
                </QueryTable.Content>
              </div>
              <DialogFooter className="gap-3">
                <DialogClose asChild>
                  <Button variant="link" disabled={isSubmitting}>
                    {t("emailRecipients.addUsers.cancel")}
                  </Button>
                </DialogClose>
                <EmailRecipientsAddUsersSubmitButton
                  onSubmit={handleSubmit}
                  isSubmitting={isSubmitting}
                />
              </DialogFooter>
            </div>
          </QueryTable>
        </DataTableQueryContextProvider>
      </DialogContent>
    </Dialog>
  );
};
